import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Reveal } from '../components/Reveal';
import { PhoneFrame } from '../components/PhoneFrame';
import { SnakExperience } from '../components/experience/SnakExperience';
import { PitchActions } from '../components/pitch/PitchActions';
import { FormatToggle } from '../components/pitch/FormatToggle';
import { ConceptOverlay } from '../components/pitch/ConceptOverlay';
import { SignalsOverlay } from '../components/pitch/SignalsOverlay';
import { StoryboardOverlay } from '../components/pitch/StoryboardOverlay';
import { PlayOverlay } from '../components/pitch/PlayOverlay';
import type { Format } from '../types/snak';
interface PitchProps {
  showProgress: boolean;
  autoplayInterlude: boolean;
}
type Layer = 'none' | 'concept' | 'signals' | 'storyboard' | 'play';
const PHONE_LABEL = 'Live mobile prototype of the Meet the Filteks Snak';
export function Pitch({
  showProgress,
  autoplayInterlude
}: PitchProps) {
  const [layer, setLayer] = useState<Layer>('none');
  const [format, setFormat] = useState<Format>('mobile');
  const close = () => setLayer('none');
  return <div className="relative flex min-h-screen w-full flex-col bg-[#F7F5F0] font-body antialiased" data-id="element-487" data-mp="1i70bxgpe0h5k">
      <div className="flex items-center justify-between gap-4 px-8 pt-6 lg:px-14" data-id="element-488" data-mp="1478tn3ru8elr">
        <span className="font-body text-[10px] uppercase tracking-[0.24em] text-[rgba(1,51,43,0.35)]" data-id="element-489" data-mp="195k1j8ykof1c">
          Meet the Filteks · Snakker concept
        </span>
        <FormatToggle value={format} onChange={setFormat} data-id="element-490" data-mp="21t6gfdd9363i" />
      </div>

      <main className="flex flex-1 items-center" data-id="element-491" data-mp="21viz5squ1khm">
        <div className="mx-auto grid w-full max-w-[1440px] items-center gap-14 px-8 py-12 lg:grid-cols-[minmax(0,1fr)_auto] lg:gap-20 lg:px-14 lg:py-8" data-id="element-492" data-mp="1n3ifid8ze9gb">
          <div className="max-w-[560px]" data-id="element-493" data-mp="1s1a6n51sxrjg">
            <Reveal data-id="element-494" data-mp="2fs65un8l7p0q">
              <h1 className="font-heading text-[58px] leading-[0.92] tracking-[-0.035em] text-background sm:text-[76px]" data-id="element-495" data-mp="2frmnx6ixt0mp">
                Meet the Filteks
              </h1>
            </Reveal>
            <Reveal delay={0.1} data-id="element-496" data-mp="2uel8qinagwsg">
              <p className="mt-7 max-w-[26ch] font-heading text-[21px] leading-[1.3] tracking-[-0.01em] text-[rgba(1,51,43,0.6)]" data-id="element-497" data-mp="2udk16o5hesfh">
                A playful way to turn a product family into a family people can actually
                understand.
              </p>
            </Reveal>
            <Reveal delay={0.2} className="mt-12" data-id="element-498" data-mp="2gi7tzha539ho">
              <PitchActions onPlay={() => setLayer('play')} onStoryboard={() => setLayer('storyboard')} onConcept={() => setLayer('concept')} onSignals={() => setLayer('signals')} data-id="element-499" data-mp="2bhbqv5xm3rdb" />
            </Reveal>
          </div>

          <Reveal delay={0.16} y={26} className="flex justify-center lg:justify-end" data-id="element-500" data-mp="1wsxeo4eb2qk3">
            <div className="flex flex-col items-center gap-5" data-id="element-501" data-mp="1sqarigskxtt8">
              <span className="font-body text-[10px] uppercase tracking-[0.24em] text-[rgba(1,51,43,0.35)]" data-id="element-502" data-mp="1ssn8orqik058">
                Live prototype
              </span>
              <PhoneFrame label={PHONE_LABEL} sizeClassName="h-[clamp(560px,calc(100vh-248px),840px)] w-auto aspect-[344/688]" data-id="element-503" data-mp="38dohvwr3p9r">
                <SnakExperience showProgress={showProgress} autoplayInterlude={autoplayInterlude} data-id="element-504" data-mp="c9ll89jmci1q" />
              </PhoneFrame>
              <span className="font-body text-[11px] tracking-[0.02em] text-[rgba(1,51,43,0.4)]" data-id="element-505" data-mp="3gobma2ztdzi3">
                Tap to play · Scroll inside the phone
              </span>
            </div>
          </Reveal>
        </div>
      </main>

      <footer className="px-8 pb-6 lg:px-14" data-id="element-506" data-mp="3porkoy1pagg3">
        <p className="font-body text-[10px] uppercase tracking-[0.18em] text-[rgba(1,51,43,0.25)]" data-id="element-507" data-mp="3pmx23qdwj0f6">
          Concept &amp; creative direction © Snakker™ 2026 · Confidential &amp;
          proprietary
        </p>
      </footer>

      <AnimatePresence data-id="element-508" data-mp="31z5qkp06qrey">
        {layer === 'concept' && <ConceptOverlay key="concept" format={format} onFormatChange={setFormat} onClose={close} data-id="element-509" data-mp="31xmaayej4gd6" />}
        {layer === 'signals' && <SignalsOverlay key="signals" format={format} onFormatChange={setFormat} onClose={close} data-id="element-510" data-mp="13fiqoirpgvqb" />}
        {layer === 'storyboard' && <StoryboardOverlay key="storyboard" format={format} onFormatChange={setFormat} onClose={close} data-id="element-511" data-mp="1cg78lok7zg8h" />}
        {layer === 'play' && <PlayOverlay key="play" format={format} showProgress={showProgress} autoplayInterlude={autoplayInterlude} onClose={close} data-id="element-512" data-mp="1mah6tmvuf8r4" />}
      </AnimatePresence>
    </div>;
}